// copies text using clipboard api or document.execCommand as a backup
// based off clipboard-copy: https://github.com/feross/clipboard-copy
async function copy(text: string) {
  try {
    await copyWithClipboardApi(text)
  } catch (clipboardError) {
    // use document.execCommand() as a fallback
    try {
      await copyWithExecCommand(text)
    } catch (execError) {
      throw execError || clipboardError || copyError()
    }
  }
}

// main copy method
// https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
async function copyWithClipboardApi(text: string) {
  if (!navigator?.clipboard) {
    throw copyError()
  }

  return navigator.clipboard.writeText(text)
}

// copy fallback
// https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
async function copyWithExecCommand(text: string) {
  // Put the text to copy into a <span>
  const span = document.createElement('span')
  span.textContent = text

  // Preserve consecutive spaces and newlines
  span.style.whiteSpace = 'pre'
  span.style.webkitUserSelect = 'auto'
  span.style.userSelect = 'all'

  // Add the <span> to the page
  document.body.appendChild(span)

  // Make a selection object representing the range of text selected by the user
  const selection = window.getSelection()
  const range = window.document.createRange()
  selection?.removeAllRanges()
  range.selectNode(span)
  selection?.addRange(range)

  // Copy text to the clipboard
  let success = false

  try {
    if (!window.document?.execCommand('copy')) {
      success = false
    } else {
      success = true
    }
  } catch (execError) {
    success = false
  } finally {
    // Cleanup
    selection?.removeAllRanges()
    window.document.body.removeChild(span)
  }

  if (!success) {
    throw copyError()
  }
}

function copyError(message: string = 'The operation is not allowed') {
  return new DOMException(message, 'NotAllowedError')
}

export { copy }
